/* =====================================================================
   AS FATURAS QUE VÊM DO PORTAL DE FRANQUIAS — 23/09/2026
   =====================================================================
   Em 18/09 esta página passou a LER as faturas do portal: quando o
   Infiniti responde 404, `buscarNoPortal` pergunta ao portal, que
   devolve o mesmo formato. Ficou faltando o outro lado — as ações.

   As abas de cartão e de recorrência continuavam mandando os dados para
   o `POST /api/asaasfatura` do Infiniti, o MESMO resolvedor que não
   conhece essas cobranças. Quem tentava pagar uma mensalidade da loja no
   cartão digitava o número inteiro, com CVV, e recebia "Erro ao efetuar
   pagamento" no fim. Pior do que não ter a aba, porque o cartão já tinha
   sido entregue quando o erro aparecia.

   Este arquivo concentra as chamadas ao portal para que o endereço dele
   exista em UM lugar só. Antes de existir, `PORTAL_BASE` estava escrito
   dentro da página da fatura, e a segunda chamada teria copiado a
   constante — é assim que as duas divergem quando o domínio muda.

   SEM CREDENCIAL, DE PROPÓSITO. Estas rotas são públicas no portal pelo
   mesmo motivo da leitura: quem abre um link de fatura não tem sessão —
   pode ser o dono da loja no celular, o contador, o cliente final. O que
   protege é o `payid` ser o segredo, mais o freio de tentativas do lado
   de lá. Por isso `fetch` puro, e não o `apiPlay`, que carrega o token
   da Play e aponta para outro domínio.
   ===================================================================== */
import { Fatura } from '../types/fatura';

export const PORTAL_BASE =
  process.env.REACT_APP_PORTAL_BASE || 'https://revendaplay.ragno.com.br';

/** Os dados do cartão, como o portal espera recebê-los. */
export interface CartaoPortal {
  cartao: {
    nome: string;
    numero: string;
    mes: string;
    ano: string;
    cvv: string;
  };
  titular: {
    nome: string;
    cpfCnpj: string;
    email: string;
    cep: string;
    numero: string;
    complemento?: string;
    telefone: string;
  };
  /* Opcional: ausente ou 1 = à vista, e o servidor segue pelo caminho de
     sempre. Acima de 1 ele troca a cobrança por um parcelamento. */
  parcelas?: number;
}

export interface RespostaPagamento {
  id: string | null;
  status: string | null;
  billingType: string | null;
  value: number | null;
  transactionReceiptUrl: string | null;
  origem: string | null;
  /* 200 NÃO QUER DIZER PAGO. O emissor pode exigir autenticação (3DS) ou
     o ASAAS pode segurar para análise de risco — nos dois casos a
     resposta é 200 e a cobrança continua em aberto. Quem decide é o
     `pago`, calculado no servidor; a tela não repete a lista de
     situações, senão uma fica para trás e volta a mentir "aprovado". */
  pago: boolean;
  autenticacao3ds: string | null;
  emAnalise: boolean;
  /* Só na recorrência: `falhou` significa que a fatura FOI paga e apenas
     o automático não ficou de pé. Esconder isso faria a pessoa pagar de
     novo achando que nada aconteceu. */
  recorrencia?: 'ativa' | 'falhou';
  cartaoFinal?: string | null;
  /* Só quando houve parcelamento. A cobrança original é SUBSTITUÍDA por
     um parcelamento no ASAAS, então o `id` acima já é o da 1ª parcela —
     este bloco é o que permite a tela dizer "3× de R$ 100,00" em vez de
     um "aprovado" genérico que não bate com a fatura do cartão. */
  parcelamento?: {
    id: string | null;
    parcelas: number;
    valorParcela: number;
    total: number;
  };
}

/** Esta fatura veio do portal? Quem marca é `buscarNoPortal`. */
export const ehDoPortal = (fatura?: Fatura | null): boolean =>
  Boolean(fatura?.doPortal);

/**
 * Só a mensalidade pode ir para o automático.
 *
 * As outras cinco origens são compras únicas. A adesão em especial vem
 * com kit de material — repetir a cobrança todo mês seria cobrar de novo
 * por algo entregue uma vez. O portal recusa isso de qualquer jeito; aqui
 * é para a aba nem aparecer, que é melhor do que oferecer e negar.
 */
export const aceitaRecorrencia = (fatura?: Fatura | null): boolean =>
  ehDoPortal(fatura) && fatura?.origem === 'mensalidade';

/**
 * Em quantas vezes ESTA fatura pode ser dividida no cartão. 1 = à vista.
 *
 * QUEM DECIDE É O SERVIDOR; a página só obedece. São duas regras que se
 * cruzam e as duas mudam sem aviso:
 *
 *   QUAL COBRANÇA   o parcelamento vale para adesão e pedido de chips.
 *                   Mensalidade não entra (ela se repete todo mês, e as
 *                   parcelas cairiam por cima das seguintes), e a fatura
 *                   do cliente final da loja também não.
 *   QUANTO DÁ       o teto de vezes e a parcela mínima são ajustáveis
 *                   pelo administrador. R$ 120 com mínima de R$ 50 vai
 *                   só até 2×, por mais que o teto diga 12.
 *
 * Por isso o número vem pronto em `parcelasMax`, no payload da fatura.
 * Uma tabela própria aqui ofereceria 12× num valor que só alcança 2, e a
 * recusa chegaria depois do cartão digitado.
 *
 * Ausente (fatura do Infiniti, ou portal ainda sem o campo) = 1, e o
 * seletor nem aparece — o mesmo cuidado de `aceitaRecorrencia`.
 */
export const parcelasDaFatura = (fatura?: Fatura | null): number => {
  if (!ehDoPortal(fatura)) return 1;
  const n = Number(fatura?.parcelasMax ?? 1);
  return Number.isFinite(n) && n > 1 ? Math.trunc(n) : 1;
};

/* O portal responde `{ erro }` nas recusas dele e `{ error }` no 404 que
   imita o Infiniti. Ler os dois evita cair no "erro desconhecido" bem na
   hora em que a mensagem é a parte útil — cartão recusado, CEP faltando,
   fatura já paga. */
async function chamar(
  caminho: string,
  dados: CartaoPortal,
): Promise<RespostaPagamento> {
  const resposta = await fetch(`${PORTAL_BASE}/api/publico/fatura/${caminho}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(dados),
  });

  const corpo = await resposta.json().catch(() => null);
  if (!resposta.ok) {
    const msg =
      corpo?.erro ||
      corpo?.error ||
      corpo?.mensagem ||
      'Não foi possível concluir o pagamento. Tente novamente.';
    throw new Error(String(msg));
  }
  return corpo as RespostaPagamento;
}

export const pagarNoPortal = (payId: string, dados: CartaoPortal) =>
  chamar(`${encodeURIComponent(payId)}/cartao`, dados);

export const ativarRecorrenciaNoPortal = (payId: string, dados: CartaoPortal) =>
  chamar(`${encodeURIComponent(payId)}/recorrencia`, dados);
